import { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { DictionariesService } from '@services/DictionariesService';
import { loadDictionaryItems, peekDictionaryItems } from '@services/dictionaryCache';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IDictFilter } from '@models/Forms/IForms';
import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';
import {
    dictBooleanFilter,
    dictPresetFilter,
    filterGridRowsByScript,
    filterResponseVoid,
    mergeDictFilter,
} from '@utils/dictionaryFilters';
import { IDictSettings } from '../IDictSettings';
import { loadDictScheme } from '../loadDictScheme';
import { buildDictRowLayout, dictRowMatches, IDictRowLayout } from '../dictRowView';
import { buildDictSort } from '../DictpickerModal/ModeControls/dictGridColumns';
import {
    isDictionaryLevelSelectable,
    parseDictionarySelectableLevels,
    withDictionarySelectionLevels,
} from '../dictionarySelectableLevels';
import { PICKER_SEARCH_DELAY, PICKER_TAKE } from '@atoms/PickerCombobox/pickerLimits';

/** Клиентская обработка: выборка приходит целиком, ищем на клиенте. */
const CLIENT_TAKE = 10000;

export type IDictComboboxSettings = Pick<
    IDictSettings,
    | 'dictName'
    | 'isFormData'
    | 'docId'
    | 'getFiltersAsync'
    | 'getFormValuesAsync'
    | 'getExternalDataSource'
    | 'gridAttribute'
    | 'selectableLevels'
    | 'predicatesCache'
    | 'joinedDictionaries'
    | 'useClientSideDataProcessing'
> & {
    /** Последние выбранные коды: при пустом поиске показываются отдельно, если проходят фильтры формы. */
    recentCodes: string[];
};

interface IPrepared {
    filters: IDictFilter;
    formValues: string;
    attributes: string[];
    /** Справочник — дерево: уровни выбора (`selectableLevels`) имеют смысл только у него. */
    hierarchical: boolean;
    /** Без сортировки в запросе строки пойдут по ключу документа (см. `fetchItems`). */
    orderedByDocumentKey: boolean;
    layout: IDictRowLayout;
}

interface ILoadResult {
    rows: IDictionaryData[];
    total: number;
    recent: IDictionaryData[];
}

export interface IDictComboboxItems {
    status: 'loading' | 'ready' | 'error';
    /** Строки ещё не под текущий поиск (debounce или запрос в пути): выбирать из них по Enter нельзя. */
    pending: boolean;
    /** Строки под текущий поиск (у клиентской обработки — уже отфильтрованные). */
    rows: IDictionaryData[];
    recent: IDictionaryData[];
    /** Сколько всего строк подходит: может быть больше показанных. */
    total: number;
    layout?: IDictRowLayout;
    /** Фильтры формы, вычисленные при открытии: пустой список под ними — не «справочник пуст». */
    filtered: boolean;
    retry: () => void;
    /** Строки по кодам под теми же условиями формы — дочитать выбранные, которых нет в показанной порции. */
    fetchByCodes: (codes: string[]) => Promise<IDictionaryData[]>;
}

const anyOf = (conditions: unknown[][]): unknown =>
    conditions.length === 1 ? conditions[0] : conditions.flatMap((c, i) => (i === 0 ? [c] : ['or', c]));

/** Отбор псевдосправочника: строки фильтруются на клиенте — сервер их не умеет. */
const formDataRows = (rows: IDictionaryData[], filters: IDictFilter, external?: IDictionaryData[]) => {
    let result = filters.filter ? filterResponseVoid(rows, filters) : rows;
    if (filters.script) result = filterGridRowsByScript(result, filters.script);
    if (!external?.length) return result;
    // внешние строки замещают одноимённые
    return [...result.filter((row) => !external.some((x) => x.code === row.code)), ...external];
};

/**
 * Отбор, который делает только клиент (скрипт-фильтр, отбор псевдосправочника, внешние строки, уровни дерева), по
 * первой странице терял бы подходящие строки дальше неё — тогда выборка грузится целиком, как у клиентской
 * обработки. Остальное ищется и листается на сервере — и у справочника, и у псевдосправочника.
 */
const needsWholeSelection = (s: IDictComboboxSettings, p: Pick<IPrepared, 'filters' | 'hierarchical'>) =>
    !!s.useClientSideDataProcessing ||
    !!p.filters.script ||
    (s.isFormData && (!!p.filters.filter || !!s.getExternalDataSource?.()?.length)) ||
    (p.hierarchical && parseDictionarySelectableLevels(s.selectableLevels).mode !== 'all');

/** Запрос порции строк: отбор формы, сортировка, предикаты; уровни выбора и скрипт-фильтр — на клиенте. */
function createPager(s: IDictComboboxSettings, p: IPrepared) {
    const rule = parseDictionarySelectableLevels(s.selectableLevels);
    const selectable = async (rows: IDictionaryData[]) =>
        (
            await withDictionarySelectionLevels(rows, rule, s.isFormData, (codes) =>
                DictionariesService.getTreeItemsByCode(s.dictName, codes),
            )
        ).filter((item) => isDictionaryLevelSelectable(rule, item.selectionLevel, item.parent === '', !item.hasChild));
    const formValues = 'formValues=' + encodeURIComponent(p.formValues);
    const preset = s.isFormData ? null : dictPresetFilter(p.filters);
    const sort = buildDictSort({ dictAttributes: p.attributes, gridAttribute: s.gridAttribute }).map((x) => ({
        selector: x.field,
        desc: x.dir === 'desc',
    }));
    // Псевдосправочник без своего порядка сортируется по ключу документа: первая страница большой выборки
    // обходит в базе все документы потока. По названию — и быстрее, и искать глазами проще.
    if (!sort.length && p.orderedByDocumentKey && p.layout.titleField) {
        sort.push({ selector: p.layout.titleField, desc: false });
    }
    return {
        selectable,
        formValues,
        page: async (own: unknown, take: number) => {
            const filter = dictBooleanFilter(mergeDictFilter(preset, own));
            let params = `?${getLoadOptionsQuery({ skip: 0, take, requireTotalCount: true, filter, sort })}&${formValues}`;
            if (s.predicatesCache) params += '&predicates=' + s.predicatesCache;
            if (s.joinedDictionaries?.length) params += `&joinedDictionaries=${JSON.stringify(s.joinedDictionaries)}`;
            const response = s.isFormData
                ? await DictionariesService.getGridFormdataItems(s.docId!, s.dictName, params)
                : await DictionariesService.getGridItems(s.dictName, params);
            let rows = response.data.data ?? [];
            const total = response.data.totalCount ?? rows.length;
            // отбор по кодам делает сервер, а скрипт-фильтр — только клиент
            if (p.filters.script) rows = filterGridRowsByScript(rows, p.filters.script);
            return { rows: await selectable(rows), total };
        },
    };
}

const byCodes = (codes: string[]) => anyOf(codes.map((code) => ['code', '=', code]));

async function fetchItems(
    s: IDictComboboxSettings,
    p: IPrepared,
    query: string,
    recentCodes: string[],
): Promise<ILoadResult> {
    const { selectable, formValues, page } = createPager(s, p);

    if (s.isFormData && needsWholeSelection(s, p)) {
        const response = await DictionariesService.getGridFormdataItems(s.docId!, s.dictName, `?${formValues}`);
        const rows = await selectable(formDataRows(response.data.data ?? [], p.filters, s.getExternalDataSource?.()));
        return { rows, total: rows.length, recent: [] };
    }

    const whole = needsWholeSelection(s, p);
    const search = query ? anyOf(p.layout.searchFields.map((field) => [field, 'contains', query])) : null;
    const [main, recent] = await Promise.all([
        page(search, whole ? CLIENT_TAKE : PICKER_TAKE),
        recentCodes.length ? page(byCodes(recentCodes), recentCodes.length) : null,
    ]);
    return {
        rows: main.rows,
        // выборка целиком отфильтрована на клиенте: серверный итог больше показанного
        total: whole ? main.rows.length : main.total,
        recent: recentCodes.flatMap((code) => recent?.rows.find((row) => row.code === code) ?? []),
    };
}

/**
 * Строки выпадающего списка справочника.
 *
 * Фильтры формы, `formValues` и схема готовятся параллельно один раз на открытие. Поиск идёт на сервере по
 * видимым в строке колонкам (debounce); у псевдосправочника и клиентской обработки — на клиенте по загруженной
 * выборке. Прошлый ответ по тому же ключу показывается сразу, свежий заменяет его по приходу.
 */
export function useDictComboboxItems(settings: IDictComboboxSettings, query: string): IDictComboboxItems {
    const settingsRef = useLatest(settings);
    const [prepared, setPrepared] = useState<IPrepared>();
    const [result, setResult] = useState<ILoadResult>();
    const [status, setStatus] = useState<IDictComboboxItems['status']>('loading');
    const [attempt, setAttempt] = useState(0);
    const clientMode = !!prepared && needsWholeSelection(settings, prepared);
    const trimmed = query.trim();
    const [debounced] = useDebouncedValue(trimmed, PICKER_SEARCH_DELAY);
    const serverQuery = clientMode || trimmed === '' ? '' : debounced;

    useEffect(() => {
        let cancelled = false;
        const s = settingsRef.current;
        setStatus('loading');
        Promise.all([s.getFiltersAsync(), s.getFormValuesAsync(), loadDictScheme(s.dictName, s.isFormData)]).then(
            ([filters, formValues, scheme]) => {
                if (cancelled) return;
                setPrepared({
                    filters: filters ?? {},
                    formValues,
                    attributes: scheme.attributes,
                    // вложенные уровни без записей («Страны») — плоский список
                    hierarchical: !s.isFormData && !!scheme.hasNestedItems,
                    orderedByDocumentKey: !!s.isFormData && !!scheme.orderedByDocumentKey,
                    layout: buildDictRowLayout(s.gridAttribute, scheme.attributes),
                });
            },
            () => !cancelled && setStatus('error'),
        );
        return () => {
            cancelled = true;
        };
    }, [settingsRef, attempt]);

    useEffect(() => {
        if (!prepared) return;
        let cancelled = false;
        const s = settingsRef.current;
        const recentCodes = serverQuery || s.isFormData ? [] : s.recentCodes;
        const key = JSON.stringify([
            s.isFormData ? s.docId : '',
            prepared.formValues,
            prepared.filters,
            s.predicatesCache,
            s.joinedDictionaries,
            s.selectableLevels,
            s.useClientSideDataProcessing,
            // от разметки колонок зависят поля поиска и сортировка
            prepared.layout.searchFields,
            buildDictSort({ dictAttributes: prepared.attributes, gridAttribute: s.gridAttribute }),
            serverQuery,
            recentCodes,
        ]);
        // строки псевдосправочника собираются из документов: прошлый ответ не показываем
        const cached = s.isFormData ? undefined : peekDictionaryItems<ILoadResult>(s.dictName, key);
        if (cached) {
            setResult(cached);
            setStatus('ready');
        } else {
            setStatus('loading');
        }
        const load = () => fetchItems(s, prepared, serverQuery, recentCodes);
        (s.isFormData ? load() : loadDictionaryItems(s.dictName, key, load)).then(
            (fresh) => {
                if (cancelled) return;
                setResult(fresh);
                setStatus('ready');
            },
            () => !cancelled && !cached && setStatus('error'),
        );
        return () => {
            cancelled = true;
        };
    }, [prepared, serverQuery, settingsRef]);

    const localQuery = clientMode ? trimmed : '';
    const rows = useMemo(() => {
        if (!result || !prepared || !localQuery) return result?.rows ?? [];
        return result.rows.filter((row) => dictRowMatches(prepared.layout.view(row), localQuery));
    }, [result, prepared, localQuery]);

    return {
        status,
        pending: status !== 'ready' || (!clientMode && serverQuery !== trimmed),
        rows,
        recent: result?.recent ?? [],
        total: localQuery ? rows.length : (result?.total ?? 0),
        layout: prepared?.layout,
        filtered: !!(prepared && (prepared.filters.filter || prepared.filters.script || prepared.formValues !== '{}')),
        retry: () => setAttempt((n) => n + 1),
        fetchByCodes: async (codes) =>
            prepared ? (await createPager(settingsRef.current, prepared).page(byCodes(codes), codes.length)).rows : [],
    };
}
